import { useFeatureIsOn } from "@growthbook/growthbook-react";
import { logger } from "@platform/browser-logger";
import { useCoverageOverview } from "@platform/coverage-overview";
import { BTX_CH_BENEFITS_FIELD } from "@platform/feature-flags";
import { useAutosave, useNotification } from "@platform/hooks";
import { useEffect, useRef, useState } from "react";

import { useSaveRfp } from "../api";

import type { RfpCoverage, RfpData } from "@platform/plan-grid-core";

interface UsePlanDesignerAutosaveArgs {
  rfp: RfpData;
  getCurrentCoverage: () => RfpCoverage | undefined;
  updateSelectedCoverage: (updatedCoverage?: RfpCoverage) => void;
  onLastSaved?: (savedAt: Date) => void;
}

export const usePlanDesignerAutosave = ({
  rfp,
  getCurrentCoverage,
  updateSelectedCoverage,
  onLastSaved,
}: UsePlanDesignerAutosaveArgs): {
  isSaving: boolean;
  isSavePending: boolean;
  triggerSave: VoidFunction;
  /** Resolves with the fields the server skipped for missing required values. */
  flushSave: () => Promise<string[]>;
  unsavedFields: string[];
} => {
  const [unsavedFields, setUnsavedFields] = useState<string[]>([]);
  const { showNotification } = useNotification();
  const rfpId = rfp.id;

  // With the flag off, Build edits Voluntary Life's three fixed child
  // benefit-description fields, so those must win on save over the
  // childBenefitAmountsByAge array — which is on the row either way (attached
  // unconditionally on read) but unedited. Read here, at the save itself,
  // rather than through shared module state: this is the only place that
  // knows both the current flag value and that a save is happening.
  const childAgeBandsFieldEnabled = useFeatureIsOn(BTX_CH_BENEFITS_FIELD);

  const { invalidateCompletionStatus } = useCoverageOverview({
    ecoRfpId: rfpId,
  });

  const saveRfpMutation = useSaveRfp({
    onSuccess: () => {
      invalidateCompletionStatus();
    },
    onError: (error) => {
      logger.error("Error saving RFP:", error);
    },
  });

  const doSave = async (): Promise<string[]> => {
    const currentCoverage = getCurrentCoverage();

    if (!currentCoverage) {
      logger.info("[PlanDesigner] Autosave skipped - no RFP or coverage");
      return [];
    }

    logger.info("[PlanDesigner] Autosave - saving coverage:", {
      currentCoverageType: currentCoverage.type,
      dirtyPlanIds: currentCoverage._dirtyPlanIds,
      currentCoverage,
    });

    const dirtyCountsBeforeSave = new Map<string, number>();
    for (const id of currentCoverage._dirtyPlanIds ?? []) {
      dirtyCountsBeforeSave.set(id, (dirtyCountsBeforeSave.get(id) ?? 0) + 1);
    }

    const response = await saveRfpMutation.mutateAsync({
      rfpId,
      name: rfp.name || "",
      coverages: [currentCoverage],
      benefitTransformOptions: {
        childAgeBandsAuthoritative: childAgeBandsFieldEnabled,
      },
    });

    const savedPlanIds = new Set(
      response.savedPlanIds ?? currentCoverage._dirtyPlanIds ?? [],
    );
    const skippedFields = response.skippedFields ?? [];
    setUnsavedFields(skippedFields);

    if (skippedFields.length > 0) {
      showNotification({
        message: `Changes not saved. Missing required values: ${skippedFields.join(", ")}`,
        severity: "error",
        autoHideDuration: 10000,
      });
    }

    if (savedPlanIds.size > 0) {
      onLastSaved?.(new Date());
    }

    const latestCoverage = getCurrentCoverage();
    if (latestCoverage) {
      const removalCredits = new Map<string, number>();
      for (const id of savedPlanIds) {
        removalCredits.set(id, dirtyCountsBeforeSave.get(id) ?? 0);
      }
      const keptIds: string[] = [];
      for (const id of latestCoverage._dirtyPlanIds ?? []) {
        const credits = removalCredits.get(id) ?? 0;
        if (credits > 0) {
          removalCredits.set(id, credits - 1);
        } else {
          keptIds.push(id);
        }
      }
      updateSelectedCoverage({
        ...latestCoverage,
        _dirtyPlanIds: keptIds.length > 0 ? keptIds : undefined,
      });
    }

    return skippedFields;
  };

  // Serialize every save through one chain so the debounced autosave and an
  // explicit flushSave never run concurrently. Two overlapping PATCHes would
  // race the _dirtyPlanIds reconciliation above and could drop ids that are
  // still dirty or resurrect ids the other call just saved.
  const saveChainRef = useRef<Promise<unknown>>(Promise.resolve());
  const inFlightSaveRef = useRef<Promise<string[]> | null>(null);
  const enqueueSave = (): Promise<string[]> => {
    const run = saveChainRef.current
      .catch(() => undefined) // a failed save must not stall the queue
      .then(() => doSave());
    saveChainRef.current = run;
    inFlightSaveRef.current = run;
    const clearInFlight = (): void => {
      if (inFlightSaveRef.current === run) {
        inFlightSaveRef.current = null;
      }
    };
    run.then(clearInFlight, clearInFlight);

    return run;
  };

  const { isSaving, triggerSave, cancelPendingSave } = useAutosave({
    delay: 2000,
    enabled: true,
    onSave: async () => {
      await enqueueSave();
    },
    onSuccess: () => {},
    onError: () => {},
  });

  // Persist pending edits immediately, bypassing the 2s debounce, before
  // actions that read the coverage's persisted state (Duplicate Plan Group,
  // export) and would otherwise miss edits still sitting in the timer.
  // Shares enqueueSave with the debounced path so the two never overlap.
  // A waiting save (on the debounce timer or deferred behind one in flight)
  // counts as work even with nothing dirty, because plan reorder persists
  // columnOrder via a no-dirty save. With no work left, the flush still waits
  // on a save already in flight and surfaces its failure.
  const flushSave = async (): Promise<string[]> => {
    const hadPendingSave = cancelPendingSave();
    const hasDirtyPlans = !!getCurrentCoverage()?._dirtyPlanIds?.length;
    if (hadPendingSave || hasDirtyPlans) {
      return enqueueSave();
    }
    return inFlightSaveRef.current ?? [];
  };

  const flushOnUnloadRef = useRef<() => void>(() => {});
  useEffect(() => {
    flushOnUnloadRef.current = () => {
      const coverage = getCurrentCoverage();
      if (!coverage?._dirtyPlanIds?.length) {
        return;
      }

      saveRfpMutation.mutate({
        rfpId,
        name: rfp.name || "",
        coverages: [coverage],
        benefitTransformOptions: {
          childAgeBandsAuthoritative: childAgeBandsFieldEnabled,
        },
      });
    };
  });

  useEffect(() => {
    const handleBeforeUnload = (): void => flushOnUnloadRef.current();
    window.addEventListener("beforeunload", handleBeforeUnload);

    return (): void =>
      window.removeEventListener("beforeunload", handleBeforeUnload);
  }, []);

  return {
    isSaving,
    isSavePending: saveRfpMutation.isPending,
    triggerSave,
    flushSave,
    unsavedFields,
  };
};
